import { inspectionIdentity, resourceOwnership, unmanagedResourceError, unmanagedResourceMessage } from "./results";
import { isResourceApplicable } from "./applicability";
import {
  clearFileProcessingSessionGoal,
  findMappingById,
  findTrigger,
  getGraphNodes,
  getResourceStepId,
  getAdapterQuestions,
  getDeliveryTarget,
  getAdapterSessionGoal,
  getWorkflowResources,
  MAPPING_RESOURCE_KINDS,
} from "@/features/adapters/model/helpers";
import type { AdapterApplyAnswers, AdapterDefinition, AdapterTrigger, AdapterTriggerDefinition } from "@/features/adapters/model/types";
import type {
  DeliveryResourceDefinition,
  ResourceDriverContext,
  ResourceApplyResult,
  ResourceDriver,
} from "./types";

const getTargetLabel = (
  definition: AdapterDefinition,
  delivery: Pick<AdapterTriggerDefinition, "targetQuestionKey"> | undefined,
): string =>
  getAdapterQuestions(definition).find((question) => question.key === delivery?.targetQuestionKey)?.label ??
  "A delivery target attribute";

export const buildStorageTriggerPayload = (
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
  resolveResourceId: ResourceDriverContext["resolveResourceId"],
  triggerKey?: string,
): AdapterTrigger => {
  const trigger = getWorkflowResources(definition, ["delivery"])
    .map(({ declaration }) => declaration.resource)
    .find((item) => !triggerKey || item.key === triggerKey);
  if (!trigger || !getDeliveryTarget(answers, trigger)) {
    throw new Error(`${getTargetLabel(definition, trigger)} is required.`);
  }
  const ownedId = (kinds: typeof MAPPING_RESOURCE_KINDS | readonly ["connection"], key: string) =>
    resolveResourceId(kinds, key);
  const mappingId = ownedId(MAPPING_RESOURCE_KINDS, trigger.mappingKey);
  if (!mappingId) throw new Error("The configured output mapping must exist before configuring pay outcome delivery.");
  const deltaMappingId = trigger.deltaMappingKey ? ownedId(MAPPING_RESOURCE_KINDS, trigger.deltaMappingKey) : undefined;
  if (trigger.deltaMappingKey && !deltaMappingId) {
    throw new Error("The configured delta mapping must exist before configuring pay outcome delivery.");
  }
  const connectionId = trigger.connectionKey ? ownedId(["connection"], trigger.connectionKey) : undefined;
  if (trigger.connectionKey && !connectionId) {
    throw new Error("The outbound SFTP connection must exist before creating pay outcome delivery.");
  }
  return {
    type: trigger.type,
    container: trigger.container,
    append: trigger.append,
    title: trigger.title,
    mapping: mappingId,
    ...(deltaMappingId ? { deltaMapping: deltaMappingId } : {}),
    ...(connectionId ? { connection: connectionId } : {}),
  };
};

export const deliveryResourceDriver: ResourceDriver<DeliveryResourceDefinition, "delivery"> = {
  kind: "delivery",
  setup: "automatic",
  describe: (declaration) => declaration.resource.statusContent.label,
  ensureWhenInapplicable: true,
  persistsOwnership: true,
  reportWhenInapplicable: true,
  define: (context, owner, declaration) => {
    const resource = declaration.resource;
    const applicable = isResourceApplicable(context.answers, owner, resource);
    const mappingStepId = getResourceStepId(context.definition, MAPPING_RESOURCE_KINDS, resource.mappingKey);
    if (!mappingStepId) throw new Error(`Unknown mapping resource "${resource.mappingKey}" in delivery "${resource.key}".`);
    const deltaMappingStepId = resource.deltaMappingKey
      ? getResourceStepId(context.definition, MAPPING_RESOURCE_KINDS, resource.deltaMappingKey)
      : undefined;
    const connectionStepId = resource.connectionKey
      ? getResourceStepId(context.definition, ["connection"], resource.connectionKey)
      : undefined;
    return [{
      key: owner.stepId,
      stepId: owner.stepId,
      definitionKey: resource.key,
      kind: "delivery",
      label: resource.statusContent.label,
      applicable,
      mappingStepId,
      deltaMappingStepId,
      connectionStepId,
      resource,
    }];
  },
  inspect: (context, definition, resourceId) => {
    const attributeId = getDeliveryTarget(context.answers, definition.resource) ?? resourceId;
    const mappingId = context.resolveResourceId(MAPPING_RESOURCE_KINDS, definition.resource.mappingKey);
    const deltaMappingId = definition.resource.deltaMappingKey
      ? context.resolveResourceId(MAPPING_RESOURCE_KINDS, definition.resource.deltaMappingKey)
      : undefined;
    const connectionId = definition.resource.connectionKey
      ? context.resolveResourceId(["connection"], definition.resource.connectionKey)
      : undefined;
    const match = findTrigger(
      context.release,
      attributeId,
      definition.resource.title,
      definition.resource.type,
    );
    const trigger = match?.trigger;
    const configured = Boolean(
      trigger &&
        findMappingById(context.release, mappingId) &&
        (!definition.resource.deltaMappingKey || findMappingById(context.release, deltaMappingId)) &&
        trigger.container === definition.resource.container &&
        trigger.append === definition.resource.append &&
        trigger.mapping === mappingId &&
        (!definition.resource.connectionKey || trigger.connection === connectionId) &&
        (!definition.resource.deltaMappingKey || trigger.deltaMapping === deltaMappingId),
    );
    const exists = Boolean(trigger && resourceId === attributeId);
    const conflict = Boolean(trigger && !exists);
    const complete = definition.applicable ? configured : exists;
    const state = conflict
      ? "conflict"
      : complete
        ? "ready"
        : trigger
          ? "incomplete"
          : attributeId
            ? "missing"
            : "incomplete";
    const message = !definition.applicable && exists
      ? "This delivery is no longer used by the current setup and will be removed when the integration is applied."
      : conflict
      ? unmanagedResourceMessage(`A delivery titled "${definition.resource.title}"`)
      : complete
        ? definition.resource.statusContent.ready
        : attributeId
          ? definition.resource.statusContent.pending
          : definition.resource.statusContent.targetMissing;
    const href = attributeId
      ? `/build/attributes?attributeId=${encodeURIComponent(attributeId)}`
      : "/build/attributes";

    return {
      ...inspectionIdentity(definition),
      ownership: resourceOwnership(exists, conflict),
      exists,
      complete,
      state,
      message,
      href,
      linkLabel: "Open delivery attribute",
      resourceId: attributeId,
      value: trigger,
      inputs: [],
      completeCount: 0,
      action:
        definition.applicable && !complete && !conflict
          ? { kind: "apply", label: "Set up pay outcome delivery", stepId: definition.stepId }
          : undefined,
    };
  },
  ensure: async (context, definition, resourceId): Promise<ResourceApplyResult> => {
    const previousAttributeId = resourceId;
    const targetAttributeId = getDeliveryTarget(context.answers, definition.resource);
    let release = context.release;

    if (definition.applicable) {
      buildStorageTriggerPayload(context.definition, context.answers, context.resolveResourceId, definition.resource.key);
      const destination =
        getGraphNodes(release).find((node) => node.id === targetAttributeId) ??
        getGraphNodes(context.release).find((node) => node.id === targetAttributeId);
      if (!destination) throw new Error("Selected trigger attribute could not be found.");
      const collision = destination.triggers?.find((trigger) => trigger.title === definition.resource.title && trigger.type === definition.resource.type);
      if (collision && previousAttributeId !== targetAttributeId) {
        throw unmanagedResourceError(`A delivery titled "${definition.resource.title}"`);
      }
    }

    if (previousAttributeId && (!definition.applicable || previousAttributeId !== targetAttributeId)) {
      const previousNode =
        getGraphNodes(release).find((node) => node.id === previousAttributeId) ??
        getGraphNodes(context.release).find((node) => node.id === previousAttributeId);
      const retained = (previousNode?.triggers ?? []).filter((trigger) => trigger.title !== definition.resource.title);
      if (previousNode && retained.length !== (previousNode.triggers ?? []).length) {
        release = await context.operations.updateAttribute(previousAttributeId, { triggers: retained });
      }
    }

    if (!definition.applicable) {
      if (definition.resource.sessionGoal && !getAdapterSessionGoal(context.definition, context.answers)) {
        release = await context.operations.updateRelease({
          fileProcessing: clearFileProcessingSessionGoal(release.fileProcessing),
        });
      }
      return {
        release,
        message: `Removed ${definition.label.toLowerCase()}.`,
        resourceId: null,
      };
    }
    if (!targetAttributeId) throw new Error(`${getTargetLabel(context.definition, definition.resource)} is required.`);
    const targetNode =
      getGraphNodes(release).find((node) => node.id === targetAttributeId) ??
      getGraphNodes(context.release).find((node) => node.id === targetAttributeId);
    if (!targetNode) throw new Error("Selected trigger attribute could not be found.");
    const existing = targetNode.triggers ?? [];
    const ownsExisting = Boolean(previousAttributeId === targetAttributeId && existing.some((trigger) => trigger.title === definition.resource.title));
    if (
      existing.some(
        (trigger) => trigger.title === definition.resource.title && trigger.type === definition.resource.type,
      ) &&
      !ownsExisting
    ) {
      throw new Error(
        `A delivery titled "${definition.resource.title}" already exists but is not managed by this integration.`,
      );
    }
    const payload = buildStorageTriggerPayload(context.definition, context.answers, context.resolveResourceId, definition.resource.key);
    const triggers = [
      ...existing.filter((trigger) => !(ownsExisting && trigger.title === definition.resource.title)),
      payload,
    ];
    const updatedRelease = await context.operations.updateAttribute(targetAttributeId, { triggers });
    return {
      release: updatedRelease,
      message: `Applied ${definition.label.toLowerCase()}.`,
      resourceId: targetAttributeId,
    };
  },
};
